/////////////////////////////////////////////////////////////////////////////////////////////////////////
//  컴포넌트명 : ChatRequestForm (1:1 상담 요청 작성)
//  설명 : 사용자가 상담요청(상담방)을 만드는 폼 - 제목 + 첫 문의 내용(+첨부 최대 5개). 요청을 보내면 상담방이
//        "승인대기(W)"로 만들어지고, 담당 상담사가 방을 클릭해 입장(=승인)해야 대화가 시작됨.
//        X9003M1(1:1상담톡 탭)과 mainFrame의 플로팅 상담패널(ChatWidget)이 같은 폼을 씀.
//  개발이력 : 2026.09.20  최초개발
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config'; // API 기본 URL
import '../styles/chatDesk.css'; // 상담톡 전용 스타일
import { useMsgBoxModal } from './CommonModal'; // 공통 모달
import ChatPendingFiles from './ChatPendingFiles'; // 전송 대기 첨부파일 칩(이미지 썸네일)
import { getPastedImageFiles } from '../utils/clipboardUtils'; // 클립보드 이미지 붙여넣기

interface ChatRequestFormProps {
  onCreated: (roomId: number) => void;   // 요청 생성 완료(승인대기 방 생성됨)
  onCancel?: () => void;                 // 작성 취소(목록으로 복귀 등) - 전달하지 않으면 취소 버튼을 표시하지 않음
  compact?: boolean;                     // 미니패널용 좁은 배치
}

const MAX_ATTACH_COUNT = 5;     // 서버 multer 제한과 동일
const MAX_ATTACH_SIZE_MB = 20;  // 서버 제한과 동일

const ChatRequestForm: React.FC<ChatRequestFormProps> = ({ onCreated, onCancel, compact }) => {
  const { showMsgBox } = useMsgBoxModal();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 첨부 대기 목록에 파일 추가(파일선택/클립보드 붙여넣기 공용)
  const addFiles = (picked: File[]) => {
    if (picked.length === 0) return;
    const tooBig = picked.find(f => f.size > MAX_ATTACH_SIZE_MB * 1024 * 1024);
    if (tooBig) {
      showMsgBox({ title: '알림', msgText: `[${tooBig.name}] 파일 용량이 너무 큽니다(파일당 최대 ${MAX_ATTACH_SIZE_MB}MB).`, showBtn: 'OK', msgType: 'warning' });
      return;
    }
    const merged = [...files];
    picked.forEach(f => { if (merged.findIndex(p => p.name === f.name && p.size === f.size) < 0) merged.push(f); });
    if (merged.length > MAX_ATTACH_COUNT) {
      showMsgBox({ title: '알림', msgText: `한 번에 최대 ${MAX_ATTACH_COUNT}개까지 첨부할 수 있습니다.`, showBtn: 'OK', msgType: 'warning' });
    }
    setFiles(merged.slice(0, MAX_ATTACH_COUNT));
  };
  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    addFiles(picked);
  };
  // 문의 내용 입력란에서 Ctrl+V - 텍스트 없이 이미지만 있으면(화면캡처 등) 첨부 대기 목록에 넣고, 텍스트가 있으면 기본 붙여넣기
  const onPasteContent = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const imgs = getPastedImageFiles(e);
    if (imgs.length === 0) return;
    e.preventDefault();
    addFiles(imgs);
  };

  // 요청 보내기 - FormData는 Content-Type을 직접 지정하지 않음(boundary 누락 방지)
  const submit = async () => {
    if (sending) return;
    if (!title.trim()) { showMsgBox({ title: '알림', msgText: '상담 제목을 입력하세요.', showBtn: 'OK', msgType: 'warning' }); return; }
    if (!content.trim() && files.length === 0) { showMsgBox({ title: '알림', msgText: '문의 내용을 입력하거나 파일을 첨부하세요.', showBtn: 'OK', msgType: 'warning' }); return; }
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('content', content.trim());
      files.forEach(f => formData.append('file', f));
      const res = await axios.post(`${API_BASE_URL}/za/apiX9003M1Create`, formData);
      const rs = res.data?.rsResults;
      if (!rs?.success) {
        showMsgBox({ title: '알림', msgText: rs?.msgTxt || '상담 요청을 보내지 못했습니다.', showBtn: 'OK', msgType: 'warning' });
        return;
      }
      onCreated(Number(rs.room_id));
    } catch (e: any) {
      showMsgBox({ title: '알림', msgText: e?.response?.data?.msgText || '상담 요청을 보내지 못했습니다.', showBtn: 'OK', msgType: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <section className={`chatDesk-chat chatDesk-form${compact ? ' compact' : ''}`}>
      <div className="chatDesk-chat-head">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="title">새 상담 요청</div>
          <div className="sub">문의 내용을 남기면 상담사가 확인 후 입장해서 상담을 시작합니다.</div>
        </div>
      </div>
      <div className="chatDesk-form-body">
        <label className="chatDesk-form-label">제목</label>
        <input
          className="chatDesk-form-input"
          value={title}
          maxLength={100}
          placeholder="상담 제목을 입력하세요"
          onChange={e => setTitle(e.target.value)}
        />
        <label className="chatDesk-form-label">문의 내용</label>
        <textarea
          className="chatDesk-form-input chatDesk-form-text"
          value={content}
          maxLength={4000}
          placeholder="궁금한 내용을 자세히 적어주세요 (이미지 붙여넣기 가능)"
          onChange={e => setContent(e.target.value)}
          onPaste={onPasteContent}
        />
        <div className="chatDesk-form-files">
          <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={onPickFiles} />
          <button type="button" className="chatDesk-head-btn" onClick={() => fileInputRef.current?.click()}><i className="fas fa-paperclip" /> 파일 첨부</button>
          <span className="chatDesk-hint" style={{ marginTop: 0 }}>최대 {MAX_ATTACH_COUNT}개, 파일당 {MAX_ATTACH_SIZE_MB}MB</span>
        </div>
        <ChatPendingFiles files={files} onRemove={f => setFiles(prev => prev.filter(p => p !== f))} />
        <div className="chatDesk-form-actions">
          {onCancel && <button type="button" className="chatDesk-head-btn" onClick={onCancel}>취소</button>}
          <button type="button" className="chatDesk-head-btn primary" disabled={sending} onClick={submit}>{sending ? '보내는 중...' : '요청 보내기'}</button>
        </div>
      </div>
    </section>
  );
};

export default ChatRequestForm;
